/**
 * API Client Utility
 *
 * Centralized HTTP client for API requests with VITE_BASE_URL integration
 */

import { getAuthHeader } from '@/services/api/tokenStorage';

const getBaseUrl = (): string => {
  const baseUrl = import.meta.env.VITE_BASE_URL;
  if (!baseUrl) {
    console.warn('[API Client] VITE_BASE_URL is not configured');
    return '';
  }
  return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
};

interface ApiRequestOptions extends RequestInit {
  params?: Record<
    string,
    string | number | boolean | (string | number | boolean)[]
  >;
}

/**
 * Make an API request with proper error handling
 */
export async function apiRequest<T = unknown>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const baseUrl = getBaseUrl();
  const url = new URL(`${baseUrl}${endpoint}`);

  // Add query parameters if provided
  if (options.params) {
    Object.entries(options.params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // Handle array params (e.g., sort=field1,desc&sort=field2,asc)
        value.forEach((item) => {
          url.searchParams.append(key, String(item));
        });
      } else {
        url.searchParams.append(key, String(value));
      }
    });
  }

  const { params, ...fetchOptions } = options;

  // Set default headers
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...fetchOptions.headers,
  };

  // Add auth token from localStorage (set during login)
  const authHeader = getAuthHeader();
  if (authHeader) {
    (headers as Record<string, string>)['Authorization'] = authHeader;
  }

  try {
    const response = await fetch(url.toString(), {
      ...fetchOptions,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `API Error: ${response.status} ${response.statusText}`
      );
    }

    // Handle empty responses (204 No Content)
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  } catch (error) {
    console.error(`[API Client] Request failed for ${endpoint}:`, error);
    throw error;
  }
}

/**
 * GET request
 */
export function apiGet<T = unknown>(
  endpoint: string,
  options?: Omit<ApiRequestOptions, 'method'>
): Promise<T> {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'GET',
  });
}

/**
 * POST request
 */
export function apiPost<T = unknown>(
  endpoint: string,
  data?: unknown,
  options?: Omit<ApiRequestOptions, 'method' | 'body'>
): Promise<T> {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * PUT request
 */
export function apiPut<T = unknown>(
  endpoint: string,
  data?: unknown,
  options?: Omit<ApiRequestOptions, 'method' | 'body'>
): Promise<T> {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * DELETE request
 */
export function apiDelete<T = unknown>(
  endpoint: string,
  options?: Omit<ApiRequestOptions, 'method'>
): Promise<T> {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'DELETE',
  });
}
